import dayjs, { Dayjs } from 'dayjs';
import { Job } from '../generated/gql-types';

type JobWithOptionalSchedule = Job & {
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
};

/**
 * Calendar day from a job form (`YYYY-MM-DD`, or a `Date` that may have come from Yup/JSON
 * parsing an ISO date-only string as UTC midnight). Always interpreted as **local** midnight
 * for that civil date — matches mobile `Date` / `startOf('day')` range anchors.
 */
export function parseScheduleDateLocal(input: string | Date): Dayjs {
  if (typeof input === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(input.trim());
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const dayOfMonth = Number(match[3]);
      return dayjs(new Date(year, monthIndex, dayOfMonth, 0, 0, 0, 0));
    }
  }
  if (input instanceof Date) {
    return dayjs(
      new Date(
        input.getFullYear(),
        input.getMonth(),
        input.getDate(),
        0,
        0,
        0,
        0,
      ),
    );
  }
  return dayjs(input).startOf('day');
}

/**
 * Parse scheduled shift clock times from `job.days.startTime` / `job.days.endTime`.
 * Values may be `HH:mm` or compact `HHmm`. Use for schedule templates only — not for
 * daySequence `clockInTime` / `clockOutTime` (those are unix-ms instants; use
 * {@link formatInJobTimezone} instead).
 */
export function parseJobDayClockTime(
  time: string | null | undefined,
): { hour: number; minute: number } | null {
  if (!time) return null;
  const trimmed = time.trim();
  let hour: number;
  let minute: number;
  if (trimmed.includes(':')) {
    const [h, m] = trimmed.split(':').map((s) => s.trim());
    hour = Number(h);
    minute = Number(m);
  } else if (/^\d{3,4}$/.test(trimmed)) {
    const padded = trimmed.padStart(4, '0');
    hour = Number(padded.slice(0, 2));
    minute = Number(padded.slice(2, 4));
  } else {
    return null;
  }
  if (
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { hour, minute };
}

/**
 * Format `job.days.startTime` / `endTime` as `HH:mm` for time pickers and forms.
 * Do not pass unix-ms timestamps here — use {@link formatInJobTimezone}.
 */
export function formatJobDayClockTime(
  time: string | null | undefined,
): string | null {
  const parts = parseJobDayClockTime(time);
  if (!parts) {
    return null;
  }
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
}

export function jobDaysOvernight(
  days: Job['days'] | null | undefined,
): boolean {
  if (!days?.startTime || !days?.endTime) return false;
  const start = parseJobDayClockTime(days.startTime);
  const end = parseJobDayClockTime(days.endTime);
  if (!start || !end) return false;
  return (
    end.hour < start.hour ||
    (end.hour === start.hour && end.minute < start.minute)
  );
}

/**
 * First calendar day of the job schedule (for labels), honouring optional
 * {@link JobWithOptionalSchedule.scheduleStartDate} when present.
 */
export function getJobDisplayScheduleStartDay(
  job: Job | null | undefined,
): Dayjs | null {
  if (!job?.startDate) return null;
  const ext = job as JobWithOptionalSchedule;
  const startMs = ext.scheduleStartDate ?? job.startDate;
  return dayjs(Number(startMs)).startOf('day');
}

/**
 * Last calendar day used when labelling "schedule end" for overnight multi-day jobs
 * without an explicit schedule end (contract {@link Job.endDate} is the morning after
 * the last shift). Mirrors mobile {@code getJobTiming} schedule-end handling.
 */
export function getJobDisplayScheduleEndDay(
  job: Job | null | undefined,
): Dayjs | null {
  if (!job?.endDate) return null;
  const ext = job as JobWithOptionalSchedule;
  const scheduleEndSource = dayjs(
    Number(ext.scheduleEndDate ?? job.endDate),
  ).startOf('day');
  const scheduleStartDay = getJobDisplayScheduleStartDay(job);
  if (!scheduleStartDay) return scheduleEndSource;
  const overnight = jobDaysOvernight(job.days);
  const hasExplicitScheduleEnd = !!ext.scheduleEndDate;
  if (
    !hasExplicitScheduleEnd &&
    overnight &&
    scheduleEndSource.isAfter(scheduleStartDay)
  ) {
    return scheduleEndSource.subtract(1, 'day');
  }
  return scheduleEndSource;
}

/**
 * Scheduled shift start/end instants for a local calendar anchor (e.g. day sequence {@code startDateISO}),
 * including rolling end past midnight for overnight shifts.
 */
export function getShiftScheduleInstantsForAnchorDate(
  job: Job | null | undefined,
  anchorDateISO: string,
): { start: Dayjs; end: Dayjs } | null {
  if (!job?.days?.startTime || !job?.days?.endTime) return null;
  const startHour = Number(job.days.startTime.slice(0, 2)) || 0;
  const startMinute = Number(job.days.startTime.slice(-2)) || 0;
  const endHour = Number(job.days.endTime.slice(0, 2)) || 0;
  const endMinute = Number(job.days.endTime.slice(-2)) || 0;
  const overnight = jobDaysOvernight(job.days);
  const anchor = dayjs(anchorDateISO, 'YYYY-MM-DD').startOf('day');
  const start = anchor.hour(startHour).minute(startMinute).second(0);
  let end = anchor.hour(endHour).minute(endMinute).second(0);
  if (overnight && !end.isAfter(start)) {
    end = end.add(1, 'day');
  }
  return { start, end };
}

/** Unix-ms string at start of first schedule day (for {@code formatDate}). */
export function getJobDisplayScheduleStartDayMs(
  job: Job | null | undefined,
): string | undefined {
  const d = getJobDisplayScheduleStartDay(job);
  return d ? d.startOf('day').valueOf().toString() : undefined;
}

/** Unix-ms string at start of schedule end calendar day (for {@code formatDate}). */
export function getJobDisplayScheduleEndDayMs(
  job: Job | null | undefined,
): string | undefined {
  const d = getJobDisplayScheduleEndDay(job);
  return d ? d.startOf('day').valueOf().toString() : undefined;
}

/**
 * One-shift length in hours from {@link Job.days} or {@link Job.shiftDurationMinutes}.
 */
export function getShiftDurationHoursFromJobDays(
  job: Pick<Job, 'days' | 'shiftDurationMinutes'> | null | undefined,
): number | null {
  const minutesRaw = job?.shiftDurationMinutes;
  const minutes =
    minutesRaw != null && Number.isFinite(Number(minutesRaw))
      ? Number(minutesRaw)
      : NaN;
  if (Number.isFinite(minutes) && minutes > 0) {
    return Math.round((minutes / 60) * 100) / 100;
  }
  const days = job?.days;
  if (!days?.startTime || !days?.endTime) return null;
  const startParts = parseJobDayClockTime(days.startTime);
  const endParts = parseJobDayClockTime(days.endTime);
  if (!startParts || !endParts) return null;
  const start = dayjs()
    .hour(startParts.hour)
    .minute(startParts.minute)
    .second(0)
    .millisecond(0);
  let end = dayjs()
    .hour(endParts.hour)
    .minute(endParts.minute)
    .second(0)
    .millisecond(0);
  if (!end.isAfter(start)) {
    end = end.add(1, 'day');
  }
  return Number(end.diff(start, 'hour', true).toFixed(2));
}

/**
 * Matches mobile {@code getDurationInUnits("hours", "hr")} labelling: "1 hr" vs "5 hrs".
 */
export function formatShiftDurationHoursLabel(
  hours: number | null | undefined,
): string | null {
  if (hours == null || Number.isNaN(hours)) return null;
  const rounded = Number(Number(hours).toFixed(2));
  const unit = rounded === 1 ? 'hr' : 'hrs';
  return `${Number(hours).toFixed(2)} ${unit}`;
}
